import {GetServerSidePropsContext} from "next";
import {ReactElement, useMemo} from "react";
import type {NextPageWithLayout} from "@pages/_app";
import {Layout} from "@components/layouts/layout";
import {PluginsSortableList} from "@components/seo/PluginsSortableList";
import {Plugin} from "@hooks/plugins/plugin";
import {IPluginsDataResponse} from "@hooks/plugins/IPluginsDataResponse";
import Link from "@components/elements/DocumentLink";
import {getDb} from "../../server/api/db";
import {PluginsGetter} from "../../server/api/data/PluginsGetter";
import {runAfterResponse} from "@utils/afterResponse";
import {formatNumber} from "@utils/format";
import {useStableNow} from "@hooks/useStableNow";
import {CACHE_WARM_HEADERS, shouldWarmCache} from "@utils/cacheWarm";

const MAX_PLUGINS_ON_HUB_PAGE = 60;

const trimPluginForHub = (candidate: any) => {
    const trimmed = {...candidate};
    trimmed.banners = undefined;
    trimmed.changelog = undefined;
    trimmed.description = undefined;
    trimmed.faq = undefined;
    trimmed.installation = undefined;
    trimmed.reviews = [];
    trimmed.screenshots = undefined;
    trimmed.icons = Array.isArray(trimmed.icons) ? trimmed.icons.slice(0, 1) : trimmed.icons;
    return trimmed;
};

const slugToTitle = (slug: string): string => {
    const normalized = slug.replace(/[-_]+/g, " ").trim();
    if (!normalized) return slug;
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=3600, stale-while-revalidate=86400'
    );

    const renderedAt = Date.now();
    const tagSlug = String(context.params?.tag ?? "").trim().toLowerCase();
    if (!tagSlug) {
        return {notFound: true};
    }

    const db = getDb();
    const getter = new PluginsGetter();
    const response: IPluginsDataResponse = await getter.get({
        db,
        tags: [tagSlug],
        page: 1,
        per_page: MAX_PLUGINS_ON_HUB_PAGE,
    }) as IPluginsDataResponse;

    const plugins = response.data?.plugins ?? [];
    if (!plugins.length) {
        return {notFound: true};
    }

    const tagLabel = (() => {
        const match = plugins[0]?.tags?.find((tag: any) => String(tag.slug ?? "").toLowerCase() === tagSlug);
        return match?.label ? String(match.label) : slugToTitle(tagSlug);
    })();

    // Warm CDN cache for top plugin pages after response is sent
    const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;
    if (baseUrl && plugins.length > 0 && shouldWarmCache(context.req)) {
        runAfterResponse(context.res, async () => {
            const topPluginIds = plugins.slice(0, 5).map((p: any) => p.pluginid).filter(Boolean);
            await Promise.allSettled(
                topPluginIds.map((id: string) =>
                    fetch(`${baseUrl}/plugins/${id}`, { method: 'GET', headers: CACHE_WARM_HEADERS }).catch(() => {})
                )
            );
        });
    }

    return {
        props: {
            tagSlug,
            tagLabel,
            totalPlugins: response.data?.total_plugins ?? null,
            stringifyPlugins: JSON.stringify(plugins.map(trimPluginForHub)),
            renderedAt,
        },
    };
};

const TagHubPage: NextPageWithLayout = (props: any) => {
    const now = useStableNow(props.renderedAt);
    const plugins = useMemo(() => {
        if (!props.stringifyPlugins) return [];
        try {
            const parsed = JSON.parse(props.stringifyPlugins);
            if (!Array.isArray(parsed)) return [];
            return parsed.map((plugin: any) => Plugin.processApiResponse(plugin, now ?? props.renderedAt));
        } catch {
            return [];
        }
    }, [props.stringifyPlugins, now, props.renderedAt]);

    return (
        <div className="flex flex-col gap-6 p-4 md:p-8 max-w-6xl mx-auto">
            <div className="flex flex-col gap-2">
                <h1 className="text-2xl md:text-4xl font-bold">
                    WordPress {props.tagLabel} plugins
                </h1>
                <p className="text-gray-700 dark:text-gray-300">
                    Browse {props.totalPlugins ? formatNumber(props.totalPlugins) : "top"} {props.tagLabel} plugins and sort by installs, downloads, ratings, update activity, or maintenance score.
                </p>
                <div className="flex flex-row flex-wrap gap-4 text-sm">
                    <Link
                        href={`/plugins?tags=${props.tagSlug}`}
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        Open full database with this tag
                    </Link>
                </div>
            </div>

            <PluginsSortableList plugins={plugins} nowTimestamp={now}/>
        </div>
    );
};

TagHubPage.getLayout = function getLayout(page: ReactElement, props: any) {
    const baseUrl = String(process.env.NEXT_PUBLIC_WEBSITE_URL || "");
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const canonicalUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/tag/${props.tagSlug}` : `/tag/${props.tagSlug}`;
    const imageUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/catspyjamas.jpg` : "/catspyjamas.jpg";

    return (
        <Layout
            title={`WordPress ${props.tagLabel} Plugins`}
            description={`Browse WordPress ${props.tagLabel} plugins with sortable stats for downloads, ratings, maintenance, support, and compatibility.`}
            url={canonicalUrl}
            image={imageUrl}
        >
            {page}
        </Layout>
    );
};

export default TagHubPage;
